import React, { useState, useEffect, useCallback } from 'react';
import api from './axiosConfig';
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination,
  TextField, MenuItem, Button, Chip, IconButton, Collapse,
  CircularProgress, Alert, Card, CardContent, Grid, Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  ExpandMore as ExpandIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  VerifiedUser as VerifiedIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const AuditTrail = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    table_name: '',
    action: '',
    start_date: '',
    end_date: ''
  });
  const [expandedRow, setExpandedRow] = useState(null);
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        )
      };
      const response = await api.get('/audit/trail', { params });
      setLogs(response.data.audit_logs);
      setTotalCount(response.data.total);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError('Failed to load audit trail. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const response = await api.get('/audit/trail/summary', { params });
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [filters.start_date, filters.end_date]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      params.format = 'hmrc';
      
      const response = await api.get('/audit/trail/export', { params });
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], 
        { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export:', err);
      setError('Failed to export audit trail. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      table_name: '',
      action: '',
      start_date: '',
      end_date: ''
    });
    setPage(0);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return <AddIcon fontSize="small" />;
      case 'update': return <EditIcon fontSize="small" />;
      case 'delete': return <DeleteIcon fontSize="small" />;
      case 'submit': return <SendIcon fontSize="small" />;
      default: return null;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'success';
      case 'update': return 'primary';
      case 'delete': return 'error';
      case 'submit': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    return status === 'success' ? 'success' : 'error';
  };

  const formatResourceName = (name) => {
    if (!name) return '';
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const renderDiff = (oldValue, newValue) => {
    if (!oldValue && !newValue) return null;
    
    const changes = [];
    const allKeys = new Set([
      ...Object.keys(oldValue || {}),
      ...Object.keys(newValue || {})
    ]);
    
    allKeys.forEach(key => {
      const oldVal = oldValue?.[key];
      const newVal = newValue?.[key];
      
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({ key, oldVal, newVal });
      }
    });
    
    if (changes.length === 0) return null;
    
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Changed Fields:
        </Typography>
        {changes.map(({ key, oldVal, newVal }) => (
          <Box key={key} sx={{ mb: 1, pl: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              {key}:
            </Typography>
            {oldVal !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    textDecoration: 'line-through', 
                    color: 'error.main',
                    backgroundColor: 'error.lighter',
                    px: 1,
                    borderRadius: 1
                  }}
                >
                  {typeof oldVal === 'object' ? JSON.stringify(oldVal) : String(oldVal)}
                </Typography>
              </Box>
            )}
            {newVal !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'success.main',
                    backgroundColor: 'success.lighter',
                    px: 1,
                    borderRadius: 1
                  }}
                >
                  {typeof newVal === 'object' ? JSON.stringify(newVal) : String(newVal)}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Audit Trail
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete record of all financial data changes for MTD compliance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchLogs} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            Export for HMRC
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {summary.total_events}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Events
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {summary.by_action?.create || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Records Created
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main">
                  {summary.by_action?.update || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Records Updated
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">
                  {summary.by_action?.delete || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Records Deleted
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FilterIcon color="action" />
          <Typography variant="subtitle1" fontWeight={500}>
            Filters
          </Typography>
          {(filters.table_name || filters.action || filters.start_date || filters.end_date) && (
            <Button 
              size="small" 
              startIcon={<ClearIcon />} 
              onClick={handleClearFilters}
            >
              Clear All
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            label="Resource Type"
            value={filters.table_name}
            onChange={(e) => {
              setFilters(f => ({ ...f, table_name: e.target.value }));
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="incomes">Income</MenuItem>
            <MenuItem value="income">Income (Daily)</MenuItem>
            <MenuItem value="expenditures">Expenses</MenuItem>
            <MenuItem value="expenditure">Expense</MenuItem>
            <MenuItem value="vat_returns">VAT Returns</MenuItem>
            <MenuItem value="employee_income">Employee Income</MenuItem>
          </TextField>
          
          <TextField
            select
            label="Action"
            value={filters.action}
            onChange={(e) => {
              setFilters(f => ({ ...f, action: e.target.value }));
              setPage(0);
            }}
            sx={{ minWidth: 120 }}
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="create">Created</MenuItem>
            <MenuItem value="update">Updated</MenuItem>
            <MenuItem value="delete">Deleted</MenuItem>
            <MenuItem value="submit">Submitted</MenuItem>
          </TextField>
          
          <TextField
            type="date"
            label="From"
            value={filters.start_date}
            onChange={(e) => {
              setFilters(f => ({ ...f, start_date: e.target.value }));
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          
          <TextField
            type="date"
            label="To"
            value={filters.end_date}
            onChange={(e) => {
              setFilters(f => ({ ...f, end_date: e.target.value }));
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {!loading && logs.length === 0 && (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="text.secondary">
              No audit logs found for the selected filters.
            </Typography>
          </Box>
        )}
        
        {!loading && logs.length > 0 && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell width={40}></TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>Record ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>User</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow
                      hover
                      onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <IconButton size="small">
                          <ExpandIcon
                            sx={{
                              transform: expandedRow === log.id ? 'rotate(180deg)' : 'none',
                              transition: '0.2s'
                            }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getActionIcon(log.action)}
                          label={log.action}
                          size="small"
                          color={getActionColor(log.action)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatResourceName(log.table_name)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {log.record_id || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          size="small"
                          color={getStatusColor(log.status)}
                          variant="filled"
                          sx={{ minWidth: 70 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.user_id || 'System'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0 }}>
                        <Collapse in={expandedRow === log.id}>
                          <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                  Event Details
                                </Typography>
                                <Box sx={{ pl: 2 }}>
                                  <Typography variant="body2">
                                    <strong>Event Type:</strong> {log.event_type || '-'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>IP Address:</strong> {log.ip_address || '-'}
                                  </Typography>
                                  {log.event_hash && (
                                    <Tooltip title="Integrity verified">
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <VerifiedIcon color="success" fontSize="small" />
                                        <Typography variant="caption" color="success.main">
                                          Hash: {log.event_hash.substring(0, 16)}...
                                        </Typography>
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                {log.details && Object.keys(log.details).length > 0 && (
                                  <>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                      Additional Details
                                    </Typography>
                                    <Box sx={{ pl: 2 }}>
                                      <pre style={{ 
                                        fontSize: 12, 
                                        overflow: 'auto',
                                        margin: 0,
                                        backgroundColor: '#fff',
                                        padding: 8,
                                        borderRadius: 4
                                      }}>
                                        {JSON.stringify(log.details, null, 2)}
                                      </pre>
                                    </Box>
                                  </>
                                )}
                              </Grid>
                            </Grid>
                            
                            {/* Show diff for updates */}
                            {log.action === 'update' && (log.old_value || log.new_value) && (
                              <Box sx={{ mt: 2 }}>
                                {renderDiff(log.old_value, log.new_value)}
                              </Box>
                            )}
                            
                            {/* Show old value for deletes */}
                            {log.action === 'delete' && log.old_value && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: 'error.main', fontWeight: 600 }}>
                                  Deleted Record:
                                </Typography>
                                <pre style={{ 
                                  fontSize: 12, 
                                  overflow: 'auto',
                                  backgroundColor: '#ffebee',
                                  padding: 8,
                                  borderRadius: 4
                                }}>
                                  {JSON.stringify(log.old_value, null, 2)}
                                </pre>
                              </Box>
                            )}
                            
                            {/* Show new value for creates */}
                            {log.action === 'create' && log.new_value && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: 'success.main', fontWeight: 600 }}>
                                  Created Record:
                                </Typography>
                                <pre style={{ 
                                  fontSize: 12, 
                                  overflow: 'auto',
                                  backgroundColor: '#e8f5e9',
                                  padding: 8,
                                  borderRadius: 4
                                }}>
                                  {JSON.stringify(log.new_value, null, 2)}
                                </pre>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </TableContainer>
    </Box>
  );
};

export default AuditTrail;